import type { EntityStatus } from '@ms/yammer-data-support';
import { getStatusForId as getStatus } from '@ms/yammer-data-support';
import createCachedSelector from 're-reselect';

import type { User, Users, YammerState } from '../../state/types';
import { createFindById, createFindByIdThrows } from '../findById';
import {
  getCurrentMtoHubNetwork,
  getIsMtoAvailable,
  getIsMtoEnabled,
  getMultiTenantOrganization,
} from '../multiTenantOrganization/selectors';
import { findById as findNetwork } from '../networks/selectors';
import { getCurrentNetworkId, getCurrentSessionUser } from '../session/selectors';
import { getUserOriginNetworkId } from '../userOriginNetworks/selectors';

/**
 * @deprecated This causes performance issues by triggering re-renders whenever the entity gets updated.
 * Use **getUserField** instead.
 */
export const findById = createFindById('users');
/**
 * @deprecated This causes performance issues by triggering re-renders whenever the entity gets updated.
 * Use **getUserFieldThrows** instead.
 */
export const findByIdThrows = createFindByIdThrows('users');

type GetUserField = <T extends keyof User>(state: YammerState, userId: User['id'], fieldName: T) => User[T] | undefined;
export const getUserField: GetUserField = (state, userId, fieldName) => {
  const user = findById(state, userId);
  if (!user) {
    return undefined;
  }

  return user[fieldName];
};

type GetUserFieldThrows = <T extends keyof User>(state: YammerState, userId: User['id'], fieldName: T) => User[T];
export const getUserFieldThrows: GetUserFieldThrows = (state, userId, fieldName) => {
  const user = findByIdThrows(state, userId);

  return user[fieldName];
};

type GetStatusForId = (state: YammerState, id: User['id']) => EntityStatus;
export const getStatusForId: GetStatusForId = (state, id) => getStatus(state.users, id);

type IsExternal = (state: YammerState, id: User['id']) => boolean;
export const isExternal: IsExternal = (state, id) => {
  const thisNetworkId = getCurrentNetworkId(state);
  const user = findById(state, id);

  if (!user) {
    return false;
  }

  return user.networkId !== thisNetworkId;
};

type GetExternalNetworkName = (state: YammerState, id: User['id']) => string | null;
export const getExternalNetworkName: GetExternalNetworkName = (state, id) => {
  if (!isExternal(state, id) || hasSameOriginAsCurrentUser(state, id)) {
    return null;
  }
  const { networkId } = findByIdThrows(state, id);

  return findNetwork(state, networkId)?.displayName || null;
};

type GetUsersState = (state: YammerState) => Users;
export const getUsersState: GetUsersState = (state) => state.users;

type GetFulfilledUsers = (state: YammerState) => Record<string, User>;
export const getFulfilledUsers: GetFulfilledUsers = (state) => state.users.fulfilled.byId;

const getUsersKey = (_state: YammerState, userIds: User['id'][]): string => userIds.join(',');
type FindUsers = (state: YammerState, userIds: User['id'][]) => User[];
export const findUsers: FindUsers = createCachedSelector(
  (_state: YammerState, userIds: User['id'][]) => userIds,
  getFulfilledUsers,
  (userIds, fulfilledUsers) =>
    userIds.reduce<User[]>((acc, userId) => {
      const user = fulfilledUsers[userId];

      if (user) {
        return [...acc, user];
      }

      return acc;
    }, [])
)(getUsersKey);

type GetUserDatabaseId = (state: YammerState, userId: User['id']) => User['databaseId'];
export const getUserDatabaseId: GetUserDatabaseId = (state, userId) => findByIdThrows(state, userId).databaseId;

type GetUserOfficeId = (state: YammerState, userId: User['id']) => User['officeUserId'];
export const getUserOfficeId: GetUserOfficeId = (state, userId) => findByIdThrows(state, userId).officeUserId;

type GetUserTelemetryId = (state: YammerState, userId: User['id']) => User['telemetryId'];
export const getUserTelemetryId: GetUserTelemetryId = (state, userId) => findByIdThrows(state, userId).telemetryId;

type GetUserSashImageUrl = (state: YammerState, userId: User['id']) => User['sashImageUrl'];
export const getUserSashImageUrl: GetUserSashImageUrl = (state, userId) => findByIdThrows(state, userId).sashImageUrl;

type GetUserSashCampaignId = (state: YammerState, userId: User['id']) => User['sashCampaignId'];
export const getUserSashCampaignId: GetUserSashCampaignId = (state, userId) =>
  findByIdThrows(state, userId).sashCampaignId;

type GetCurrentUserLegacyId = (state: YammerState) => User['databaseId'];
export const getCurrentUserLegacyId: GetCurrentUserLegacyId = (state) => {
  const { id: currentUserId } = getCurrentSessionUser(state);

  return findByIdThrows(state, currentUserId).databaseId;
};

type GetCurrentUserTelemetryId = (state: YammerState) => User['telemetryId'];
export const getCurrentUserTelemetryId: GetCurrentUserTelemetryId = (state) => {
  const { id: currentUserId } = getCurrentSessionUser(state);

  return findByIdThrows(state, currentUserId).telemetryId;
};

type IsCurrentUser = (state: YammerState, id: User['id']) => boolean;
export const isCurrentUser: IsCurrentUser = (state, id) => getCurrentSessionUser(state).id === id;

type GetUserSubtitle = (state: YammerState, id: User['id']) => string;
export const getUserSubtitle: GetUserSubtitle = (state, id) => {
  const { email, jobTitle } = findByIdThrows(state, id);

  return jobTitle && jobTitle.trim() ? jobTitle : email;
};

type GetCurrentUser = (state: YammerState) => User;
export const getCurrentUser: GetCurrentUser = (state) => findByIdThrows(state, getCurrentSessionUser(state).id);

type GetCurrentUserFieldThrows = <T extends keyof User>(state: YammerState, fieldName: T) => User[T];
export const getCurrentUserFieldThrows: GetCurrentUserFieldThrows = (state, fieldName) => {
  const user = findByIdThrows(state, getCurrentSessionUser(state).id);

  return user[fieldName];
};

type GetCurrentUserField = <T extends keyof User>(state: YammerState, fieldName: T) => User[T] | undefined;
export const getCurrentUserField: GetCurrentUserField = (state, fieldName) => {
  const user = findById(state, getCurrentSessionUser(state).id);
  if (!user) {
    return undefined;
  }

  return user[fieldName];
};

type IsGuestUserThrows = (state: YammerState, id: User['id']) => boolean;
export const isGuestUserThrows: IsGuestUserThrows = (state, id) => {
  const { isGuest } = findByIdThrows(state, id);

  return isGuest;
};

type IsGuestUser = (state: YammerState, id: User['id']) => boolean;
export const isGuestUser: IsGuestUser = (state, id) => {
  const user = findById(state, id);

  if (!user) {
    return false;
  }

  return user.isGuest;
};

type IsGuestOrExternalUser = (state: YammerState, userId: User['id']) => boolean;
export const isGuestOrExternalUser: IsGuestOrExternalUser = (state, userId) => {
  const isGuest = isGuestUserThrows(state, userId);
  const isExternalUser = isExternal(state, userId);
  const isCurrentUserGuest = getCurrentSessionUser(state).isGuest;
  const isSameOrigin = hasSameOriginAsCurrentUser(state, userId);

  return !isSameOrigin && ((!isCurrentUserGuest && isGuest) || isExternalUser);
};

type HasSameOriginAsCurrentUser = (state: YammerState, userId: User['id']) => boolean;
const hasSameOriginAsCurrentUser: HasSameOriginAsCurrentUser = (state, userId) => {
  const originNetworkId = getUserOriginNetworkId(state, userId);
  const currentUser = getCurrentSessionUser(state);
  const currentUserId = currentUser.id;
  const currentNetworkId = getCurrentNetworkId(state);
  const currentUserOriginNetworkId = getUserOriginNetworkId(state, currentUserId) || currentNetworkId;

  return !!originNetworkId && currentUserOriginNetworkId === originNetworkId;
};

type IsMtoCrossNetworkUser = (state: YammerState, userId: User['id']) => boolean;
export const isMtoCrossNetworkUser: IsMtoCrossNetworkUser = (state, userId) => {
  const currentUser = getCurrentSessionUser(state);
  const user = findById(state, userId);
  const currentNetworkId = getCurrentNetworkId(state);
  const userOriginNetworkId = getUserOriginNetworkId(state, userId);

  const fromSameNetwork = !user?.isGuest && user?.networkId === currentNetworkId;

  if (currentUser.isGuest || fromSameNetwork) {
    return false;
  }

  return !!(
    (user?.isGuest && userOriginNetworkId !== undefined) ||
    (!user?.isGuest && userOriginNetworkId !== currentNetworkId)
  );
};

type IsMtoHubUser = (state: YammerState, userId: User['id']) => boolean;
export const isMtoHubUser: IsMtoHubUser = (state, userId) => {
  if (!getIsMtoAvailable(state) || !getIsMtoEnabled(state)) {
    return false;
  }

  const user = findById(state, userId);

  if (!user) {
    return false;
  }

  const userOriginNetworkId = getUserOriginNetworkId(state, userId);
  const userNetworkId = user.isGuest ? userOriginNetworkId : user.networkId;
  const hubNetworkId = getCurrentMtoHubNetwork(state)?.id;

  return hubNetworkId === userNetworkId;
};

type IsMtoSpokeUser = (state: YammerState, userId: User['id']) => boolean;
export const isMtoSpokeUser: IsMtoSpokeUser = (state, userId) => {
  if (!getIsMtoAvailable(state) || !getIsMtoEnabled(state)) {
    return false;
  }

  const user = findById(state, userId);

  if (!user) {
    return false;
  }

  const userOriginNetworkId = getUserOriginNetworkId(state, userId);
  const userNetworkId = user.isGuest ? userOriginNetworkId : user.networkId;
  const hubNetworkId = getCurrentMtoHubNetwork(state)?.id;

  const mtoOrganization = getMultiTenantOrganization(state);

  return (
    mtoOrganization?.networks.some((network) => network.nodeId !== hubNetworkId && network.nodeId === userNetworkId) ||
    false
  );
};

type IsMtoUser = (state: YammerState, userId: User['id']) => boolean;
export const isMtoUser: IsMtoUser = (state, userId) => {
  if (!getIsMtoAvailable(state)) {
    return false;
  }

  const user = findById(state, userId);
  if (!user) {
    return false;
  }

  const mtoOrganization = getMultiTenantOrganization(state);

  const userOriginNetworkId = getUserOriginNetworkId(state, userId);
  const networkId = user.isGuest ? userOriginNetworkId : user.networkId;

  return mtoOrganization?.networks.some((network) => network.nodeId === networkId) || false;
};
